import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Disc3, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMG =
  "https://images.unsplash.com/photo-1541126274323-dbac58d14741?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxkaiUyMHBhcnR5JTIwY2x1YiUyMHB1YiUyMG5pZ2h0bGlmZSUyMG11c2ljfGVufDB8fHx8MTc9MTg4MjE5OXww&ixlib=rb-4.1.0&q=85";

const MESI = ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"];
const GIORNI = ["DOMENICA", "LUNEDÌ", "MARTEDÌ", "MERCOLEDÌ", "GIOVEDÌ", "VENERDÌ", "SABATO"];

function isToday(d) {
  const t = new Date();
  return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear();
}

export default function DjSection() {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/events`)
      .then((r) => setEvents(r.data.map((e) => ({ ...e, d: new Date(`${e.date}T19:00:00`) }))))
      .catch(() => setEvents([]));
  }, []);

  return (
    <section id="dj-set" data-testid="dj-set-section" className="relative py-24 sm:py-32 overflow-hidden grain">
      <img
        src={IMG}
        alt="Consolle del DJ durante un set serale"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-[#0D0D0F]/88" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <p className="text-xs font-mono2 tracking-[0.25em] uppercase text-[#F49D37] flex items-center gap-2">
            <Disc3 size={14} className="animate-spin [animation-duration:3s]" />
            Ogni weekend
          </p>
          <h2 className="font-display uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.9] mt-5 max-w-4xl">
            DJ set fino
            <br />
            <span className="text-[#D34E36]">alle 2 di notte</span>
          </h2>
          <p className="text-base sm:text-lg text-stone-300 leading-relaxed mt-7 max-w-xl">
            Venerdì, sabato e domenica la piazza si accende: consolle accesa, cocktail della
            casa e birra che scorre fino a chiusura.
          </p>
        </motion.div>

        <p
          data-testid="eventi-calendar-label"
          className="text-xs font-mono2 tracking-[0.25em] uppercase text-stone-400 mt-16 mb-6"
        >
          Le prossime serate
        </p>
        {events !== null && events.length === 0 && (
          <div
            data-testid="events-empty-note"
            className="bg-[#1C1C22]/90 backdrop-blur border border-[#2A2A34] rounded-2xl p-8 text-center max-w-xl"
          >
            <p className="font-display uppercase text-2xl tracking-wide">Calendario in aggiornamento</p>
            <p className="text-sm text-stone-400 mt-2">
              Le prossime serate vengono annunciate su Instagram e qui sul sito.
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {(events || []).map((e, i) => (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
              data-testid={`event-card-${i}`}
              className={`relative bg-[#1C1C22]/90 backdrop-blur border rounded-2xl p-5 sm:p-7 hover:-translate-y-1 transition-transform duration-300 ${
                e.tag ? "border-[#D34E36]" : "border-[#2A2A34]"
              }`}
            >
              {isToday(e.d) && (
                <span className="absolute top-4 right-4 bg-[#D34E36] text-[#F5F2EB] text-[9px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full">
                  Stasera
                </span>
              )}
              {e.tag && !isToday(e.d) && (
                <span className="absolute top-4 right-4 bg-[#F49D37] text-black text-[9px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full">
                  {e.tag}
                </span>
              )}
              <p className="font-display text-5xl sm:text-6xl text-[#F49D37] leading-none">{e.d.getDate()}</p>
              <p className="font-mono2 text-[10px] sm:text-[11px] tracking-[0.2em] uppercase text-stone-400 mt-2">
                {GIORNI[e.d.getDay()]} · {MESI[e.d.getMonth()]}
              </p>
              <h3 className="font-display uppercase text-xl sm:text-2xl tracking-wide mt-4">{e.title}</h3>
              {e.desc && <p className="text-sm text-stone-400 leading-relaxed mt-2">{e.desc}</p>}
              <p className="flex items-center gap-2 text-[11px] font-mono2 uppercase tracking-widest text-stone-500 mt-5 pt-4 border-t border-[#2A2A34]">
                <Clock size={13} className="text-[#D34E36]" /> 19:00 – 02:00
              </p>
            </motion.div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mt-12">
          <p className="text-sm text-stone-400">
            Aperti 19:00 – 02:00 · Martedì chiuso · Le serate a tema vengono annunciate su{" "}
            <a
              href="https://www.instagram.com/lateresina_nizza_monferrato"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="eventi-instagram-link"
              className="text-[#F49D37] hover:text-[#D34E36] transition-colors"
            >
              Instagram
            </a>
          </p>
          <a
            href="#contatti"
            data-testid="dj-set-cta-button"
            className="shrink-0 bg-[#D34E36] hover:bg-[#F49D37] hover:text-black text-[#F5F2EB] font-bold uppercase tracking-widest text-xs px-7 py-3.5 rounded-full transition-colors"
          >
            Prenota per la serata
          </a>
        </div>
      </div>
    </section>
  );
}
